import React, { useEffect, useReducer, useRef, useState } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import StyledSnackbar from '../NewUI/Components/CustomUIElements/StyledSnackbar';
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Popover,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import {
  CalendarMonth as CalendarMonthIcon,
  ChevronLeftRounded as ChevronLeftRoundedIcon,
  ChevronRightRounded as ChevronRightRoundedIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Sort as SortIcon,
  ViewList as ViewListIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { withScoutTheme } from '../ThemeContext/ThemeContext';
import CreateEvent from './CreateEvent/CreateEvent';
import Settings from './Settings';
import { sharedClasses } from '../NewUI/Components/CustomUIElements/sharedClasses';
import { InitialScoutCalendarState, ScoutCalendarReducer } from './reducer';
import { classes } from './styles';
import { IEvent } from './types';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ViewEvent from './ViewEvent';
import CreateButton from '../NewUI/Components/Buttons/CreateButton';
import ListView from './ListView';
import EventCreated from './EventCreated';
import { getPlatform } from '../NewUI/utils/device-detect';
import { useEvents } from '../NewUI/hooks';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import moment from 'moment-timezone';

import { userTimeZone } from '../NewUI/utils/formatToUserTimeZone';
import newDateFromString from '../shared/dateHelper';
import { formatUserPermissions } from '../NewUI/Components/Utilities/formatUserPermissions';

interface IScoutCalendarProps {
  currentUser: { id: number; time_zone: string | null };
  token: string;
  newApplicationEnabled: boolean;
}

moment.tz.setDefault(userTimeZone);
dayjs.extend(isoWeek);
const localizer = momentLocalizer(moment);

const URL_PARAMS = new URL(window.location.href);

function ScoutCalendar({ currentUser, token, newApplicationEnabled }: IScoutCalendarProps) {
  const { isMobile } = getPlatform();
  const [ScoutCalendarState, dispatch] = useReducer(
    ScoutCalendarReducer,
    InitialScoutCalendarState
  );
  const { weekStart, createEventObject } = ScoutCalendarState;
  const root = useRef(null);
  const [viewDate, setViewDate] = useState<Date>(new Date());
  const [showList, setShowList] = useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLDivElement | null>(null);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState<HTMLDivElement | null>(null);
  const [eventCreated, setEventCreated] = useState<boolean>(false);
  const [calendarView, setCalendarView] = useState<boolean>(!isMobile);
  const { data: events, isLoading: loadingEvents } = useEvents({
    weekStart: weekStart,
    onError: (message: string) =>
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: `There was an error getting events, ${message}`, state: 'error' }
      })
  });
  const permissions = formatUserPermissions();

  function initialData() {
    const URL = window.location.pathname.split('/');
    URL[URL.length - 2] === 'show' && setViewDate(dayjs(URL[URL.length - 1]).toDate());
    URL[URL.length - 1] === 'show' && handleSelectSlot();
    URL_PARAMS.search === '?new' && handleSelectSlot();
    if (URL_PARAMS.search === '?new_multiple') {
      handleSelectSlot();
      dispatch({ type: 'CREATE_MULTIPLE_EVENTS', payload: true });
    }
  }

  useEffect(() => {
    initialData();

    return () => {
      moment.tz.setDefault();
    };
  }, []);

  useEffect(() => getWeekStart(), [viewDate]);

  function getWeekStart() {
    const today = dayjs(viewDate).isoWeekday();
    const startDay =
      today === 0 ? dayjs(viewDate).toDate() : dayjs(viewDate).startOf('week').toDate();
    dispatch({ type: 'SET_WEEK_START', payload: startDay });
  }

  const getMonthsName = () => {
    if (dayjs(weekStart).format('MMMM') !== dayjs(weekStart).add(6, 'd').format('MMMM')) {
      return `${dayjs(weekStart).format('MMM')} - ${dayjs(weekStart)
        .add(6, 'd')
        .format('MMM')} ${dayjs(weekStart).add(6, 'd').format('YYYY')}`;
    } else {
      return dayjs(weekStart).format('MMMM YYYY');
    }
  };

  function handleSelectEvent(event: IEvent, withoutModal?: boolean) {
    dispatch({
      type: 'SET_CREATE_EVENT_OBJECT',
      payload: {
        ...createEventObject,
        eventName: event.subject,
        eventDescription: event.note || '',
        maxCandidates: String(event.max_candidates),
        recruiters: event.attendees
          .filter((a) => a.type === 'User')
          .map((a) => ({
            id: a.id,
            name: a.name,
            checked: false,
            attendeeId: a.attendee_id,
            state: a.state
          })),
        eventType: event.event_type,
        duration: String(event.duration_in_minutes),
        eventDate: newDateFromString(event.begins_at),
        eventId: event.id,
        candidates: event.attendees.filter((a) => a.type === 'Candidate'),
        dates: [newDateFromString(event.begins_at)],
        owner: event.owner,
        jobs: event.jobs || []
      }
    });
    !withoutModal && dispatch({ type: 'SET_SHOW_VIEW_EVENT_MODAL', payload: true });
  }

  function handleSelectSlot(slot?: { slots: Date[] }) {
    const selectedDate = slot ? slot.slots[0] : new Date();

    let year, month, date, hour, minute;

    if (slot) {
      // Convert slot to user timezone and extract date components
      const slotMoment = moment(selectedDate).tz(userTimeZone);
      year = slotMoment.year();
      month = slotMoment.month();
      date = slotMoment.date();
      hour = slotMoment.hour();
      minute = slotMoment.minute();
    } else {
      // For Create Event button, get current date in user timezone
      const now = moment().tz(userTimeZone);
      year = now.year();
      month = now.month();
      date = now.date();
      hour = now.hour();
      minute = now.minute();
    }

    // Create a new date with the selected date and appropriate time in user timezone
    const userTimezoneTime = moment.tz(
      `${year}-${String(month + 1).padStart(2, '0')}-${String(date).padStart(2, '0')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`,
      userTimeZone
    );
    const preservedDate = userTimezoneTime.toDate();

    dispatch({
      type: 'SET_CREATE_EVENT_OBJECT',
      payload: { ...createEventObject, eventDate: preservedDate, dates: [preservedDate] }
    });
    dispatch({ type: 'SET_SHOW_CREATE_EVENT_MODAL', payload: true });
  }

  function CustomEvent({ event }: { event: IEvent }) {
    const eventLength = dayjs(event.end).diff(dayjs(event.start), 'minute');
    const sessionLength = event.duration_in_minutes;

    if (!eventLength) return <span />;

    const attendees = event.attendees.filter((attendee) => attendee.type !== 'User');

    return (
      <Box
        className={`
          event-container
          ${eventLength <= 10 ? 'small-event' : ''}
          ${sessionLength <= 20 ? 'small-session' : ''}
          ${'blue'}
        `}
      >
        <Box className="event-bg">
          <Box className={`event-details ${eventLength <= 30 ? 'truncate' : ''}`}>
            <Box
              className="event-title"
              sx={{ fontSize: '11px', fontWeight: 'bold', color: '#333333' }}
            >
              {event.subject}
            </Box>
          </Box>
          <Box className="session-container">
            <Box
              key={event.id}
              className={`session-inner ${!(sessionLength < 10) ? 'hoverable' : ''}`}
              style={{
                height: `${(event.duration_in_minutes / eventLength) * 100}%`
              }}
            >
              <Box
                id="event-card"
                className="session-length"
                onClick={() => handleSelectEvent(event)}
              >
                <Box
                  className={`session-element ${
                    attendees.filter((attendee) => attendee.state === 'accepted').length >=
                    event.max_candidates
                      ? 'booked'
                      : ''
                  }`}
                >
                  <span
                    className="session-candidate"
                    style={{ fontSize: eventLength <= 10 ? '11px' : '12px' }}
                  >
                    {attendees.length > 0
                      ? attendees.length === 1
                        ? `${attendees[0].name}`
                        : `${attendees.length} candidates`
                      : 'No bookings'}
                  </span>
                  {sessionLength >= 20 && (
                    <span className="session-time">
                      {dayjs(event.begins_at).tz(userTimeZone).format('HH:mma')}
                    </span>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  const handleCreateEvent = (multiple: boolean) => {
    multiple && dispatch({ type: 'CREATE_MULTIPLE_EVENTS', payload: true });
    setActionsAnchorEl(null);
    handleSelectSlot();
    URL_PARAMS.search = multiple ? '?new_multiple' : '?new';
    window.history.pushState(null, '', URL_PARAMS.href);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box ref={root} className="chronos-calendar">
        <Box sx={sharedClasses.pageHeader}>
          <Typography variant="h1">Events</Typography>
          {(permissions?.['Events']?.['Create / Edit Events'] ||
            permissions?.['Events']?.['Create Multiple Events']) && (
            <CreateButton
              id="create-event-main-button"
              ariaLabel="Add new events"
              onClick={(e) => {
                setActionsAnchorEl(e.currentTarget);
              }}
              isOpen={Boolean(actionsAnchorEl)}
            />
          )}
          <Popover
            open={Boolean(actionsAnchorEl)}
            anchorEl={actionsAnchorEl}
            onClose={() => {
              setActionsAnchorEl(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{ '& .MuiPaper-root': { marginTop: '12px', borderRadius: '6px' } }}
          >
            <Stack sx={classes.addNewEventsPopover}>
              {permissions?.['Events']?.['Create / Edit Events'] && (
                <Stack onClick={() => handleCreateEvent(false)} id="create-an-event-button">
                  Create An Event
                </Stack>
              )}
              {permissions?.['Events']?.['Create Multiple Events'] && (
                <Stack onClick={() => handleCreateEvent(true)} id="create-multiple-events-button">
                  Create Multiple Events
                </Stack>
              )}
            </Stack>
          </Popover>
        </Box>
        <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack sx={{ width: '150px', flexDirection: 'row' }}>
            <Button
              id="today-button"
              sx={sharedClasses.genericButtonSecondary}
              onClick={() => setViewDate(new Date())}
            >
              Today
            </Button>
          </Stack>
          <Stack>
            <Box
              id="select-month"
              sx={classes.calendarHeader}
              onClick={(e) => {
                setCalendarAnchorEl(e.currentTarget);
              }}
            >
              {getMonthsName()}
            </Box>
            <Popover
              open={Boolean(calendarAnchorEl)}
              anchorEl={calendarAnchorEl}
              onClose={() => {
                setCalendarAnchorEl(null);
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              sx={{ '& .MuiPaper-root': { marginTop: 1 } }}
            >
              <Stack sx={{ padding: 1 }}>
                <CloseIcon
                  sx={sharedClasses.closeIcon}
                  onClick={() => {
                    setCalendarAnchorEl(null);
                  }}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateCalendar
                    views={['month', 'year']}
                    openTo="month"
                    sx={classes.dateCalendar}
                    onChange={(val: Dayjs | null) => val && setViewDate(val.toDate())}
                  />
                </LocalizationProvider>
              </Stack>
            </Popover>
          </Stack>
          <Stack sx={classes.viewAndSettingsContainer}>
            {!isMobile && (
              <Tooltip placement="top" title={calendarView ? 'List view' : 'Calendar view'} arrow>
                <Button
                  variant="outlined"
                  id="calendar-list-view-button"
                  sx={classes.viewAndSettingsButton}
                  onClick={() => setCalendarView(!calendarView)}
                >
                  {calendarView ? (
                    <ViewListIcon sx={{ color: '#084D6D' }} fontSize="small" />
                  ) : (
                    <CalendarMonthIcon sx={{ color: '#084D6D' }} fontSize="small" />
                  )}
                </Button>
              </Tooltip>
            )}
            <Tooltip placement="top" title="Download iCal" arrow>
              <Button
                variant="outlined"
                id="download-ical-button"
                sx={classes.viewAndSettingsButton}
                onClick={() => window.open(`${window.location.origin}/ical.ics?token=${token}`)}
              >
                <DownloadIcon sx={{ color: '#084D6D' }} fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip placement="top" title="Filter events" arrow>
              <Button
                variant="outlined"
                id="filter-events-button"
                sx={classes.viewAndSettingsButton}
                onClick={() => setShowList(!showList)}
              >
                <SortIcon sx={{ color: '#084D6D' }} fontSize="small" />
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
        <Stack sx={classes.weekPeriodContainer}>
          <ChevronLeftRoundedIcon
            id="previous-week-button"
            sx={classes.calendarArrows}
            onClick={() => setViewDate(dayjs(viewDate).subtract(1, 'w').toDate())}
          />
          <Box sx={{ fontSize: '14px' }}>
            {dayjs(weekStart).format('DD MMMM')} - {dayjs(weekStart).add(6, 'd').format('DD MMMM')}
          </Box>
          <ChevronRightRoundedIcon
            id="next-week-button"
            sx={classes.calendarArrows}
            onClick={() => setViewDate(dayjs(viewDate).add(1, 'w').toDate())}
          />
        </Stack>
        <Stack sx={classes.timezoneInfo}>
          <InfoIcon fontSize="small" />
          Your Calendar displays in{' '}
          <b>
            GMT ({dayjs().tz(userTimeZone).format('Z')}) {userTimeZone}
          </b>{' '}
          time.
          <Link href={`${window.location.origin}/admin/settings`} target="_blank" underline="hover">
            Edit time zone
          </Link>
        </Stack>
        {loadingEvents || !events ? (
          <Stack sx={{ paddingTop: 20, alignItems: 'center' }}>
            <CircularProgress size={45} sx={{ color: '#004E70' }} />
          </Stack>
        ) : (
          <>
            {calendarView ? (
              <Calendar
                events={events}
                date={viewDate}
                localizer={localizer}
                scrollToTime={moment().subtract(90, 'm').toDate()}
                defaultView={Views.WEEK}
                step={30}
                timeslots={1}
                selectable={true}
                onSelectSlot={(e) =>
                  permissions?.['Events']?.['Create / Edit Events'] && handleSelectSlot(e)
                }
                onSelecting={() => false}
                onNavigate={() => false}
                components={{
                  event: CustomEvent
                }}
                formats={{
                  dayFormat: 'dddd DD/MM'
                }}
                dayPropGetter={(date) => {
                  const day = moment(date).day();
                  if (day === 0 || day === 6)
                    return { className: 'weekend', style: { backgroundColor: '#F2F2F2' } };
                  return {};
                }}
              />
            ) : (
              <ListView
                events={events}
                handleSelectEvent={handleSelectEvent}
                ScoutCalendarState={ScoutCalendarState}
                dispatch={dispatch}
              />
            )}
          </>
        )}
      </Box>
      {showList && events && (
        <Settings
          showList={showList}
          setShowList={setShowList}
          events={events}
          handleSelectEvent={handleSelectEvent}
        />
      )}
      {ScoutCalendarState.showCreateEventModal && events && (
        <CreateEvent
          events={events}
          ScoutCalendarState={ScoutCalendarState}
          dispatch={dispatch}
          setEventCreated={setEventCreated}
        />
      )}
      {ScoutCalendarState.showViewEventModal && events && (
        <ViewEvent
          events={events}
          ScoutCalendarState={ScoutCalendarState}
          dispatch={dispatch}
          currentUserId={currentUser.id}
          userTimezone={userTimeZone}
          newApplicationEnabled={newApplicationEnabled}
        />
      )}
      <EventCreated
        open={eventCreated}
        onClose={() => {
          setEventCreated(false);
          dispatch({ type: 'RESET_STATE' });
          URL_PARAMS.search = '';
          window.history.pushState(null, '', URL_PARAMS.href);
        }}
        ScoutCalendarState={ScoutCalendarState}
        userTimezone={userTimeZone}
      />
      <StyledSnackbar
        message={ScoutCalendarState.snackbar.message}
        state={ScoutCalendarState.snackbar.state}
        setSnackbarState={() =>
          dispatch({ type: 'SET_SNACKBAR', payload: { message: '', state: 'success' } })
        }
      />
    </Box>
  );
}

export default withScoutTheme(ScoutCalendar);
